import { Flex, Image } from '@iheartjane/reefer';
import type { MaskitoOptions } from '@maskito/core';
import { useMaskito } from '@maskito/react';
import { clsx } from 'clsx';

import { alwaysStartsWith } from '@jane/business-admin/util';

import colorWheel from './color-wheel.png';

import styles from './ColorInput.module.css';

export interface ColorInputProps {
  /** Accessible label for the hex input. Defaults to "Hex color". */
  ariaLabel?: string;
  'data-testid'?: string;
  disabled?: boolean;
  hasError?: boolean;
  onBlur?: () => void;
  onChange: (hex: string) => void;
  value: string;
}

const hexColorMask: MaskitoOptions = {
  mask: /^#[0-9a-fA-F]{0,6}$/,
  preprocessors: [alwaysStartsWith('#')],
};

/**
 * A compact color-picker input for manually entering a hex color or opening the
 * native OS color picker via the color-wheel icon. The value always starts with `#`.
 */
export const ColorInput = ({
  ariaLabel = 'Hex color',
  'data-testid': testId,
  disabled = false,
  hasError = false,
  onBlur,
  onChange,
  value,
}: ColorInputProps) => {
  const inputRef = useMaskito({ options: hexColorMask });

  return (
    <div
      className={clsx(styles['wrapper'], {
        [styles['wrapper__error']]: hasError,
        [styles['wrapper__disabled']]: disabled,
      })}
      data-testid={testId}
    >
      <Flex
        width={16}
        height={16}
        style={{ backgroundColor: value, borderRadius: 3 }}
        shrink={0}
      />
      <input
        aria-label={ariaLabel}
        className={styles['input']}
        disabled={disabled}
        maxLength={7}
        onBlur={onBlur}
        onInput={(e) => onChange(e.currentTarget.value)}
        ref={inputRef}
        type="text"
        value={value}
      />
      <label className={styles['iconButton']}>
        <Image
          altText="color-wheel"
          src={colorWheel}
          width="28px"
          height="28px"
        />
        <input
          aria-label={`${ariaLabel} picker`}
          className={styles['nativeColor']}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          type="color"
          value={value}
        />
      </label>
    </div>
  );
};
